import React from "react";

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  hasNext,
  hasPrev,
  className = "",
}) => {
  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();

  const PageButton = ({
    page,
    isActive = false,
    disabled = false,
    children,
  }) => (
    <button
      onClick={() => !disabled && !isActive && onPageChange(page)}
      disabled={disabled}
      className={`
        relative inline-flex items-center px-4 py-2 text-sm font-medium
        ${
          isActive
            ? "z-10 bg-blue-600 text-white border-blue-600"
            : disabled
            ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-300"
            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
        }
        border transition-colors duration-200
      `}
    >
      {children || page}
    </button>
  );

  if (totalPages <= 1) {
    return null;
  }

  return (
    <nav className={`flex items-center justify-center ${className}`}>
      <div className="flex items-center space-x-1">
        {/* Previous Button */}
        <PageButton page={currentPage - 1} disabled={!hasPrev}>
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span className="ml-1 hidden sm:inline">Previous</span>
        </PageButton>

        {/* Page Numbers */}
        {visiblePages.map((page, index) => {
          if (page === "...") {
            return (
              <span
                key={`dots-${index}`}
                className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300"
              >
                ...
              </span>
            );
          }

          return (
            <PageButton
              key={page}
              page={page}
              isActive={page === currentPage}
            />
          );
        })}

        {/* Next Button */}
        <PageButton page={currentPage + 1} disabled={!hasNext}>
          <span className="mr-1 hidden sm:inline">Next</span>
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </PageButton>
      </div>

      {/* Page Info */}
      <div className="ml-4 text-sm text-gray-700 hidden sm:block">
        Page {currentPage} of {totalPages}
      </div>
    </nav>
  );
};

export default Pagination;
